/**
 * Groups every supplier in an AuditResult into the packs sent out by the
 * "Download all" folder export. Like rows.js this is pure (no React, no
 * Firestore) so the grouping rules can be unit tested.
 *
 * One pack per supplier business: hotels, golf courses and transfer companies
 * each get their own folder and their own workbook. Records that are variants
 * of one venue ("The Belfry" and "The Belfry Groups 16+", "PGA Belfry" and
 * "PGA Belfry Groups") share a pack, since they are the same people at the
 * other end of the email.
 *
 * Resort-level packs (a hotel with its own courses) are deliberately not built:
 * nothing in the data reliably says which courses a hotel owns. The CMS links
 * are unpopulated, and the bookings only show who plays where, which is a
 * different question.
 *
 * Removed or nameless supplier records get no pack (there is nobody to send it
 * to) and are listed for the office to check instead.
 */

import { filterBySuppliers } from "./rows"
import { supplierKey, UNNAMED_SUPPLIER_NAME } from "./types"

export const PACK_FOLDERS = { hotel: "hotels", course: "golf_courses", transfer: "transfers" }

const PACK_LABELS = { hotel: "Hotel", course: "Golf course", transfer: "Transfer" }

/**
 * Lowercase, underscore-separated file-name fragment.
 */
export const slugify = (name) =>
	String(name || "")
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, "_")
		.replace(/^_+|_+$/g, "") || "supplier"

/**
 * Name used to spot variants of one venue, e.g. "The Belfry Groups 16+" and
 * "The Belfry" both become "the belfry". Drops bracketed text, anything from
 * the word "groups" onwards and group-size markers such as "16+".
 */
export const baseName = (name) =>
	String(name || "")
		.toLowerCase()
		.replace(/\(.*?\)/g, " ")
		.replace(/\bgroups?\b.*$/, " ")
		.replace(/\b\d+\s*\+/g, " ")
		.replace(/[^a-z0-9]+/g, " ")
		.trim()

/**
 * Suppliers of one kind grouped by baseName, in first-seen order.
 */
const groupByBaseName = (suppliers) => {
	const byName = new Map()
	suppliers.forEach((supplier) => {
		const name = baseName(supplier.name) || supplier.id
		if (!byName.has(name)) byName.set(name, [])
		byName.get(name).push(supplier)
	})
	return Array.from(byName.values())
}

/**
 * The shortest name in a group is the plain venue name ("The Belfry" rather
 * than "The Belfry Groups 16+"). Ties go to the busiest record.
 */
const primaryOf = (suppliers) => [...suppliers].sort((a, b) => a.name.length - b.name.length || b.bookingCount - a.bookingCount || a.name.localeCompare(b.name))[0]

const compareByName = (a, b) => a.title.localeCompare(b.title, "en", { sensitivity: "base" })

/**
 * @typedef {Object} SupplierPack
 * @property {"hotel"|"course"|"transfer"} type
 * @property {string} title         Primary supplier name
 * @property {string} folder        Sub-folder inside the export folder
 * @property {string} fileName      Including .xlsx
 * @property {string[]} keys        supplierKey of every supplier in the pack
 * @property {Object[]} suppliers   The AuditSupplierSummary records
 * @property {string} groupedBy     How the pack was put together, for the index
 */

/**
 * @typedef {Object} PackCheck
 * @property {Object} supplier      AuditSupplierSummary
 * @property {string} issue
 * @property {string} packs         File the supplier ended up in, "" for none
 */

/**
 * Give every pack a unique file name within its folder. Clashing names gain
 * the start of the primary supplier's GHD ID, or the whole ID if that still
 * clashes.
 */
const assignFileNames = (packs) => {
	const counts = new Map()
	packs.forEach((pack) => {
		const key = `${pack.folder}/${slugify(pack.title)}`
		counts.set(key, (counts.get(key) || 0) + 1)
	})

	const taken = new Set()
	packs.forEach((pack) => {
		const slug = slugify(pack.title)
		const id = slugify(primaryOf(pack.suppliers).id)
		const candidates = counts.get(`${pack.folder}/${slug}`) > 1 ? [`${slug}_${id.slice(0, 4)}`, `${slug}_${id}`] : [slug, `${slug}_${id}`]
		const name = candidates.find((candidate) => !taken.has(`${pack.folder}/${candidate}`)) || `${slug}_${id}_${taken.size}`
		taken.add(`${pack.folder}/${name}`)
		pack.fileName = `supplier_audit_${name}.xlsx`
	})
}

/**
 * Plan every pack for the folder export.
 *
 * @param {import("./types").AuditResult} result
 * @returns {{ packs: SupplierPack[], checks: PackCheck[] }}
 */
export const planSupplierPacks = (result) => {
	const checks = []

	// Removed and nameless records have nobody to send a pack to
	const sendable = result.suppliers.filter((supplier) => {
		if (supplier.missing) checks.push({ supplier, issue: "Supplier record removed - no pack", packs: "" })
		else if (!supplier.name || supplier.name === UNNAMED_SUPPLIER_NAME) checks.push({ supplier, issue: "Supplier record has no name - no pack", packs: "" })
		else return true
		return false
	})

	const packsOfKind = (kind) =>
		groupByBaseName(sendable.filter((supplier) => supplier.kind === kind))
			.map((group) => {
				const pack = {
					type: kind,
					title: primaryOf(group).name,
					folder: PACK_FOLDERS[kind],
					fileName: "",
					suppliers: group,
					keys: group.map(supplierKey),
					groupedBy: group.length > 1 ? `${group.length} records joined by name` : "Single record",
				}
				if (group.length > 1) group.forEach((supplier) => checks.push({ supplier, issue: `Joined to "${pack.title}" by name - check it belongs`, packs: "" }))
				return pack
			})
			.sort(compareByName)

	const packs = [...packsOfKind("hotel"), ...packsOfKind("course"), ...packsOfKind("transfer")]
	assignFileNames(packs)

	// Fill in where each checked supplier landed
	checks.forEach((check) => {
		const pack = packs.find((candidate) => candidate.keys.includes(supplierKey(check.supplier)))
		check.packs = pack ? `${pack.folder}/${pack.fileName}` : ""
	})

	return { packs, checks }
}

/**
 * The trimmed result a pack's workbook is built from.
 */
export const packResult = (result, pack) => filterBySuppliers(result, pack.keys)

const unique = (values) => Array.from(new Set(values.filter(Boolean)))

/**
 * Sheets for _index.xlsx: one row per pack, then the checks list.
 *
 * @param {import("./types").AuditResult} result
 * @param {{ packs: SupplierPack[], checks: PackCheck[] }} plan
 */
export const buildIndexSheets = (result, { packs, checks }) => {
	const packRows = packs.map((pack) => {
		const trimmed = packResult(result, pack)
		return {
			Folder: pack.folder,
			File: pack.fileName,
			"Pack type": PACK_LABELS[pack.type],
			Supplier: pack.title,
			"All records": pack.suppliers.map((supplier) => supplier.name).join("; "),
			Email: unique(pack.suppliers.map((supplier) => supplier.email)).join("; "),
			"GHD IDs": pack.suppliers.map((supplier) => supplier.id).join("; "),
			Bookings: trimmed.bookings.length,
			"Hotel lines": trimmed.bookings.reduce((total, booking) => total + booking.hotels.length, 0),
			Rounds: trimmed.bookings.reduce((total, booking) => total + booking.rounds.length, 0),
			"Grouped by": pack.groupedBy,
		}
	})

	const checkRows = checks.map(({ supplier, issue, packs: file }) => ({
		Supplier: supplier.name,
		Type: PACK_LABELS[supplier.kind],
		"GHD ID": supplier.id,
		Issue: issue,
		"In pack": file,
		Bookings: supplier.bookingCount,
	}))

	return [
		{ name: "Packs", rows: packRows, headers: ["Folder", "File", "Pack type", "Supplier", "All records", "Email", "GHD IDs", "Bookings", "Hotel lines", "Rounds", "Grouped by"] },
		{ name: "Checks", rows: checkRows, headers: ["Supplier", "Type", "GHD ID", "Issue", "In pack", "Bookings"] },
	]
}
